import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCalendar, FiClock, FiMapPin, FiUsers, FiArrowRight } = FiIcons;

export default function Hero() {
  const eventFacts = [
    { icon: FiCalendar, label: 'วันที่', value: '19–20 ก.ย. 69' },
    { icon: FiClock, label: 'เวลา', value: '09.30–17.00 น.' },
    { icon: FiMapPin, label: 'สถานที่', value: 'S Ratchada Hotel' },
    { icon: FiUsers, label: 'ที่นั่ง', value: 'จำกัด 39 ท่าน' },
  ];

  return (
    <section className="relative min-h-screen flex flex-col justify-center pt-20 overflow-hidden bg-white">
      <div className="absolute inset-0 pointer-events-none z-0">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-zinc-50/50 -skew-x-12 translate-x-1/4" />
        <div className="absolute top-[20%] left-[5%] w-64 h-64 border border-zinc-100 rounded-full opacity-50" />
        <div className="absolute bottom-[10%] right-[10%] w-96 h-96 border border-zinc-100 rounded-full opacity-50" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 w-full py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
            >
              <div className="inline-flex items-center gap-3 mb-10 bg-zinc-900 px-4 py-2 text-white">
                <span className="w-8 h-[2px] bg-brand-red" />
                <span className="text-[10px] font-black tracking-[0.3em] uppercase">
                  MASTERING YOUR FINANCIAL DESTINY
                </span>
              </div>

              <h1 className="text-5xl md:text-7xl lg:text-[100px] font-black text-zinc-900 mb-8 leading-[0.95] tracking-tighter">
                BAZI <br />
                <span className="text-brand-red italic">FINANCIAL &</span> <br />
                INVESTMENT
              </h1>

              <div className="flex items-center gap-6 mb-10">
                <div className="h-20 w-[3px] bg-zinc-200" />
                <p className="text-xl md:text-2xl text-zinc-500 max-w-xl leading-relaxed font-medium italic">
                  "แผนที่ความมั่งคั่งที่ในดวงคุณ" เจาะลึกวิชาปาจื้อการเงิน
                  การลงทุนเพื่อการตัดสินใจที่ถูกทาง
                </p>
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-5 mb-16">
                <a
                  href="#registration"
                  className="group w-full sm:w-auto px-12 py-6 text-sm font-black uppercase tracking-widest text-white bg-brand-red hover:bg-zinc-900 transition-all duration-500 shadow-2xl shadow-brand-red/20 flex items-center justify-center gap-3"
                >
                  ลงทะเบียนราคาพิเศษ
                  <SafeIcon
                    icon={FiArrowRight}
                    className="w-5 h-5 group-hover:translate-x-2 transition-transform"
                  />
                </a>

                <a
                  href="#curriculum"
                  className="w-full sm:w-auto px-12 py-6 text-sm font-black uppercase tracking-widest text-zinc-900 bg-white border-2 border-zinc-900 hover:bg-zinc-50 transition-all duration-300 text-center"
                >
                  ดูเนื้อหาหลักสูตร
                </a>
              </div>
            </motion.div>
          </div>

          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.8, rotate: 5 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ delay: 0.3, duration: 1, ease: 'easeOut' }}
              className="relative z-10"
            >
              <div className="aspect-[4/5] bg-zinc-100 border-4 border-zinc-900 shadow-[40px_40px_0px_0px_rgba(211,47,47,1)] overflow-hidden">
                <img
                  src="https://media-manager-c.questera.ai/greta-media/471e332637c745cac6e5f952a1669a461aea7ff8853c3b4ee7dd2b5f799ce1f00d91c0ccf6cd346bf4393cbd10a6b0de/images/aW1hZ2UvcG5n/eff875dab0d3317751d0baba23f9e4bb.png"
                  alt="Bazi Financial Analysis"
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="absolute -top-10 -right-10 w-32 h-32 bg-zinc-900 rounded-full flex items-center justify-center text-center p-4 border-4 border-white shadow-2xl transform rotate-12">
                <div className="text-white">
                  <div className="text-xs font-black uppercase tracking-widest opacity-60">
                    GEN 01
                  </div>
                  <div className="text-2xl font-black">รุ่นที่ 1</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.8 }}
          className="grid grid-cols-2 lg:grid-cols-4 bg-zinc-900 text-white shadow-2xl relative z-20"
        >
          {eventFacts.map((fact, index) => (
            <div
              key={index}
              className={`p-8 md:p-10 ${
                index !== eventFacts.length - 1
                  ? 'border-b lg:border-b-0 lg:border-r border-white/10'
                  : ''
              } group hover:bg-brand-red transition-colors duration-500 cursor-default`}
            >
              <div className="flex items-center gap-3 mb-3 text-brand-red group-hover:text-white transition-colors">
                <SafeIcon icon={fact.icon} className="w-5 h-5" />
                <span className="text-[11px] font-black uppercase tracking-[0.2em]">
                  {fact.label}
                </span>
              </div>
              <div className="text-lg md:text-xl font-black">{fact.value}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}